import { Injectable, ElementRef } from '@angular/core';
import { jsPDF } from 'jspdf';
import { Chart, registerables } from 'chart.js';
import { map, catchError, firstValueFrom } from 'rxjs';
import { ReportsService } from '../service/reports.service';
import { BinUsage } from '../constants/bin-usage';
import { ChartService } from '../service/chart.service';
import { SystemMessageService } from './system-message.service';
import { TranslateService } from '@ngx-translate/core';
import { CANADA_TAX_RATES, CanadaTaxRate } from '../constants/canada-tax';

interface BinData {
  bin_id: string;
  bin_name?: string;
  bin_gps?: string;
  bin_location: string;
  thing_name: string;
  bin_usage: string;
  bin_address: string;
  legal_name: string;
  market_segment: string;
  threshold: number;
  bin_height: number;
  total_volume: number;
}

interface SubGroup {
  device_sub_group: string;
  sub_group_name: string;
  things: { thing_name: string; tonnage: number }[];
}

interface ReportMaster {
  thing_name: string;
  timestamp: number;
  usage: string;
  market: string;
  tonnage: number;
}

@Injectable({
  providedIn: 'root',
})
export class PdfService {


  //Chart Related Arrays
  public history: any = [];
  public counts: any = [];
  public collections: any = [];
  public historyLabels: any[] = [];
  public historyData: any[] = [];
  public historyTitle: string = '';
  public collectionsLabels: any[] = [];
  public collectionsData: any[] = [];
  public collectionsTitle: string = '';
  public countsLabels: any[] = [];
  public countsData: any[] = [];
  public thingCollectionLabels: any[] = [];
  public collectionTonnage: any[] = [];
  public usageData: any[] = [];
  public marketData: any[] = [];
  public usageLabels: any[] = [];
  public marketLabels: any[] = [];
  public subgroups: SubGroup[] = [];
  public masterArray: ReportMaster[] = [];

  public usageTonnageLabels: any[] = [];
  public marketTonnageLabels: any[] = [];
  public subgroupTonnageLabels: any[] = [];

  public usageTonnageData: any[] = [];
  public marketTonnageData: any[] = [];
  public subgroupTonnageData: any[] = [];

  //Chart Instances for dynamic updates
  public countChartInstance: any;
  public tonnageChartInstance: any;
  public usageChartInstance: any;
  public marketChartInstance: any;

  public subgroupTonnageChartInstance: any;
  public marketTonnageChartInstance: any;
  public usageTonnageChartInstance: any;

  //Font Sizes
  public legendFontSize: number = 20;
  public tickFontSize: number = 30;

  //Date constraints
  public start: string = '';
  public end: string = '';

  //Chart related strings
  public countsTitle: string = '';
  public label: string = '';

  //PDF Graph Canvas Objects
  public usageCanvas!: ElementRef;
  public marketCanvas!: ElementRef;
  public countCanvas!: ElementRef;
  public tonnageCanvas!: ElementRef;
  public subgroupTonnageCanvas!: ElementRef;
  public marketTonnageCanvas!: ElementRef;
  public usageTonnageCanvas!: ElementRef;

  public things: any[] = [];

  public generatingPDF: boolean = false;

  constructor(
    private charts: ChartService,
    public reports: ReportsService,
    public systemMessage: SystemMessageService,
    private translate: TranslateService
  ) {
    Chart.register(...registerables);
  }

  private addStandardHeader(pdf: jsPDF, title: string): number {
    // Shared header banner + title used across reports/invoices.
    const icon = '../../assets/header-pdf.png'; // App header image used in existing PDFs.
    const pageWidth = pdf.internal.pageSize.getWidth(); // Use dynamic width for consistent scaling.
    const imgProps = pdf.getImageProperties(icon); // Read image size to preserve aspect ratio.
    const aspectRatio = imgProps.width / imgProps.height; // Keep original proportions.
    const headerHeight = pageWidth / aspectRatio; // Full-width banner height.
    pdf.addImage(icon, 'PNG', 0, 0, pageWidth, headerHeight); // Render header image.

    pdf.setFontSize(18);
    pdf.setTextColor(0);
    pdf.text(title, pageWidth / 2, headerHeight + 10, { align: 'center' }); // Centered title below header.
    return headerHeight;
  }

  private ensureCanvas(ref: ElementRef | undefined, name: string): HTMLCanvasElement {
    if (!ref?.nativeElement) {
      console.error(`Canvas missing: ${name}`);
      return document.createElement('canvas');
    }
    return ref.nativeElement as HTMLCanvasElement;
  }

  private buildChart(name: string, canvasRef: ElementRef, config: any, existing?: Chart): Chart | null {
    if (existing) {
      existing.destroy();
    }
    const canvas = this.ensureCanvas(canvasRef, name);
    config = {
      ...config,
      options: {
        responsive: false,
        animation: false,
        ...config?.options,
      },
    };
    try {
      return new Chart(canvas, config);
    } catch (err) {
      console.error(`[PDF] Chart init failed: ${name}`, err);
      return null;
    }
  }

  public cleanData = {
    labels: [],
    datasets: [
      {
        label: '',
        data: [],
        backgroundColor: [],
        borderColor: [],
        borderWidth: 1,
      },
    ],
  };

  //Generate PDF click event
  async savePDF() {
    await this.applyReportFilters();
  }

  //Initialize Canvas for Bin Usage Data
  async initUsageCanvas() {
    // Destroy the previous chart instance
    this.usageChartInstance = this.buildChart(
      'usage',
      this.usageCanvas,
      {
      type: 'doughnut',
      data: {
        labels: this.usageLabels,
        datasets: [
          {
            label: 'Usage Distribution',
            data: this.usageData,
            backgroundColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],
            borderColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],

            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        aspectRatio: 1,
        plugins: {
          legend: {
            display: true,
            labels: {
              font: {
                size: this.legendFontSize,
              },
            },
          },
        },
      },
    },
      this.usageChartInstance
    );
  }

  //Initialize Canvas for Market Data
  async initMarketCanvas() {
    // Destroy the previous chart instance
    this.marketChartInstance = this.buildChart(
      'market',
      this.marketCanvas,
      {
      type: 'doughnut',
      data: {
        labels: this.marketLabels,
        datasets: [
          {
            label: 'Market Distribution',
            data: this.marketData,
            backgroundColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],
            borderColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: false,
        animation: false,
        aspectRatio: 1,
        plugins: {
          legend: {
            display: true,
            labels: {
              font: {
                size: this.legendFontSize,
              },
            },
          },
        },
      },
    },
      this.marketChartInstance
    );
  }

  //Initialize Canvas for Collection Data
  async initCollectionCountsCanvas() {
    this.countChartInstance = this.buildChart('counts', this.countCanvas, {
      type: 'bar',
      data: {
        labels: this.countsLabels,
        datasets: [
          {
            label: 'Total Collections',
            data: this.countsData,
            backgroundColor: ['rgba(50, 141, 30, 0.509)'],
            borderColor: ['rgb(50, 108, 30)'],
            borderWidth: 1,
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
          y: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
        },
      },
    },
    this.countChartInstance);
  }

  //Initialize Canvas for Collected Tonnage Data
  async initTonnageCanvas() {
    this.tonnageChartInstance = this.buildChart('tonnage', this.tonnageCanvas, {
      type: 'bar',
      data: {
        labels: this.thingCollectionLabels,
        datasets: [
          {
            label: 'Total Tonnage Collected',
            data: this.collectionTonnage,
            backgroundColor: [

              'rgba(216, 191, 216)', // Pastel Purple (Thistle)

            ],
            borderColor: [

              'rgba(216, 191, 216)', // Pastel Purple (Thistle)

            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
          y: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
        },
      },
    },
    this.tonnageChartInstance);
  }

  //Initialize Canvas for Collected Tonnage Data
  async initSubgroupTonnageCanvas() {
    this.subgroupTonnageChartInstance = this.buildChart(
      'subgroupTonnage',
      this.subgroupTonnageCanvas,
      {
        type: 'bar',
        data: {
          labels: this.subgroupTonnageLabels,
          datasets: [
            {
              label: 'Total Tonnage Collected',
              data: this.subgroupTonnageData,
              backgroundColor: [
                'rgba(119, 221, 119)', // Pastel Green
                'rgba(255, 105, 97)', // Pastel Red
                'rgba(173, 216, 230)', // Pastel Blue
                'rgba(253, 253, 150)', // Pastel Yellow
                'rgba(255, 179, 255)', // Pastel Magenta
                'rgba(178, 255, 255)', // Pastel Cyan
                'rgba(199, 128, 128)', // Pastel Maroon
                'rgba(144, 238, 144)', // Light Pastel Green
                'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
                'rgba(230, 230, 170)', // Pastel Olive
                'rgba(216, 191, 216)', // Pastel Purple (Thistle)
                'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
                'rgba(224, 224, 224)', // Light Silver
                'rgba(200, 200, 200)', // Light Gray
                'rgba(255, 204, 153)', // Pastel Orange (Peach)
                'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
              ],
              borderColor: [
                'rgba(119, 221, 119)', // Pastel Green
                'rgba(255, 105, 97)', // Pastel Red
                'rgba(173, 216, 230)', // Pastel Blue
                'rgba(253, 253, 150)', // Pastel Yellow
                'rgba(255, 179, 255)', // Pastel Magenta
                'rgba(178, 255, 255)', // Pastel Cyan
                'rgba(199, 128, 128)', // Pastel Maroon
                'rgba(144, 238, 144)', // Light Pastel Green
                'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
                'rgba(230, 230, 170)', // Pastel Olive
                'rgba(216, 191, 216)', // Pastel Purple (Thistle)
                'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
                'rgba(224, 224, 224)', // Light Silver
                'rgba(200, 200, 200)', // Light Gray
                'rgba(255, 204, 153)', // Pastel Orange (Peach)
                'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              ticks: {
                font: {
                  size: this.tickFontSize,
                },
              },
            },
            y: {
              ticks: {
                font: {
                  size: this.tickFontSize,
                },
              },
            },
          },
        },
      },
      this.subgroupTonnageChartInstance
    );
  }

  //Initialize Canvas for Collected Tonnage Data
  async initUsageTonnageCanvas() {
    this.usageTonnageChartInstance = this.buildChart('usageTonnage', this.usageTonnageCanvas, {
      type: 'bar',
      data: {
        labels: this.usageTonnageLabels,
        datasets: [
          {
            label: 'Total Tonnage Collected',
            data: this.usageTonnageData,
            backgroundColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],
            borderColor: [
              'rgba(119, 221, 119)', // Pastel Green
              'rgba(255, 105, 97)', // Pastel Red
              'rgba(173, 216, 230)', // Pastel Blue
              'rgba(253, 253, 150)', // Pastel Yellow
              'rgba(255, 179, 255)', // Pastel Magenta
              'rgba(178, 255, 255)', // Pastel Cyan
              'rgba(199, 128, 128)', // Pastel Maroon
              'rgba(144, 238, 144)', // Light Pastel Green
              'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
              'rgba(230, 230, 170)', // Pastel Olive
              'rgba(216, 191, 216)', // Pastel Purple (Thistle)
              'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
              'rgba(224, 224, 224)', // Light Silver
              'rgba(200, 200, 200)', // Light Gray
              'rgba(255, 204, 153)', // Pastel Orange (Peach)
              'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
            ],
            borderWidth: 1,
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
          y: {
            ticks: {
              font: {
                size: this.tickFontSize,
              },
            },
          },
        },
      },
    },
    this.usageTonnageChartInstance);
  }

  //Initialize Canvas for Collected Tonnage Data
  async initMarketTonnageCanvas() {
    // Destroy the previous chart instance
    if (this.marketTonnageChartInstance) {
      this.marketTonnageChartInstance.destroy();
      this.marketTonnageChartInstance = null; // Clear the reference
    }

    this.marketTonnageChartInstance = this.buildChart(
      'marketTonnage',
      this.marketTonnageCanvas,
      {
        type: 'bar',
        data: {
          labels: this.marketTonnageLabels,
          datasets: [
            {
              label: 'Total Tonnage Collected',
              data: this.marketTonnageData,
              backgroundColor: [
                'rgba(119, 221, 119)', // Pastel Green
                'rgba(255, 105, 97)', // Pastel Red
                'rgba(173, 216, 230)', // Pastel Blue
                'rgba(253, 253, 150)', // Pastel Yellow
                'rgba(255, 179, 255)', // Pastel Magenta
                'rgba(178, 255, 255)', // Pastel Cyan
                'rgba(199, 128, 128)', // Pastel Maroon
                'rgba(144, 238, 144)', // Light Pastel Green
                'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
                'rgba(230, 230, 170)', // Pastel Olive
                'rgba(216, 191, 216)', // Pastel Purple (Thistle)
                'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
                'rgba(224, 224, 224)', // Light Silver
                'rgba(200, 200, 200)', // Light Gray
                'rgba(255, 204, 153)', // Pastel Orange (Peach)
                'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
              ],
              borderColor: [
                'rgba(119, 221, 119)', // Pastel Green
                'rgba(255, 105, 97)', // Pastel Red
                'rgba(173, 216, 230)', // Pastel Blue
                'rgba(253, 253, 150)', // Pastel Yellow
                'rgba(255, 179, 255)', // Pastel Magenta
                'rgba(178, 255, 255)', // Pastel Cyan
                'rgba(199, 128, 128)', // Pastel Maroon
                'rgba(144, 238, 144)', // Light Pastel Green
                'rgba(174, 198, 207)', // Pastel Navy (Blue Gray)
                'rgba(230, 230, 170)', // Pastel Olive
                'rgba(216, 191, 216)', // Pastel Purple (Thistle)
                'rgba(175, 238, 238)', // Pale Turquoise (Pastel Teal)
                'rgba(224, 224, 224)', // Light Silver
                'rgba(200, 200, 200)', // Light Gray
                'rgba(255, 204, 153)', // Pastel Orange (Peach)
                'rgba(255, 160, 160)', // Soft Red (distinct from earlier pastel red)
              ],
              borderWidth: 1,
            },
          ],
        },
        options: {
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              ticks: {
                font: {
                  size: this.tickFontSize,
                },
              },
            },
            y: {
              ticks: {
                font: {
                  size: this.tickFontSize,
                },
              },
          },
        },
      },
      },
      this.marketTonnageChartInstance
    );
  }

  //Labels for Collections Canvas
  totalCollectionsPerDevice(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Total Collections per Device';
      case '"es"':
        return 'Cobranzas totales por dispositivo';
    }

    return 'Collections totales par appareil';
  }

  //Headers for Collections Canvas
  collectionStatisticsHeader(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Collection Statistics';
      case '"es"':
        return 'Estadísticas de recaudación';
    }

    return 'Statistiques de collecte';
  }

  //Title for Collections Canvas
  collectionReport(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Collections Report';
      case '"es"':
        return 'Informe de cobranzas';
    }

    return 'Rapport de collections';
  }

  //PDF name translations
  fileNameCollectionReport(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Collections-Report';
      case '"es"':
        return 'Informe-de-cobranzas';
    }

    return 'Rapport-de-collections';
  }

  //Information Text for statistics
  infoTextStatistics(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return `This portion of the report contains some aggregate collection statistics between ${this.start} and ${this.end}.`;
      case '"es"':
        return `Esta parte del informe contiene algunas estadísticas de colección agregadas entre ${this.start} y ${this.end}.`;
    }

    return `Cette partie du rapport contient quelques statistiques agrégées de collecte entre le ${this.start} et ${this.end}.`;
  }

  //Label for Tonnage  in Statistics
  totalTonnagePerDevice(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Total Estimated Tonnage Collected per Device';
      case '"es"':
        return 'Tonelaje total estimado recolectado por dispositivo';
    }

    return 'Tonnage total estimé collecté par appareil';
  }

  //Title for Usage statistic
  usageDistribution(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Usage Distribution';
      case '"es"':
        return 'Distribución de uso';
    }
    return "Répartition de l'utilisation";
  }

  //Title for Market statistics
  marketDistribution(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Market Distribution';
      case '"es"':
        return 'Distribución del mercado';
    }

    return 'Répartition du marché';
  }

  marketTonnageDistribution(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Tonnage per Market';
      case '"es"':
        return 'Tonelaje por Mercado';
    }

    // Default to French
    return 'Tonnage par Marché';
  }

  usageTonnageDistribution(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Tonnage per Usage';
      case '"es"':
        return 'Tonelaje por Uso';
    }

    // Default to French
    return 'Tonnage par Utilisation';
  }

  subgroupTonnageDistribution(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Tonnage per Subgroup';
      case '"es"':
        return 'Tonelaje por Subgrupo';
    }

    // Default to French
    return 'Tonnage par Sous-groupe';
  }

  //Collection data Header
  collectionDataHeader(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return 'Collection Data';
      case '"es"':
        return 'Datos de recopilación';
    }

    return 'Données de collecte';
  }

  //Informational text for collection data section
  infoTextCollectionData(): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        return `This section of the report contains the parsed data for each individual collection between ${this.start} and ${this.end}.`;
      case '"es"':
        return `Esta sección del informe contiene los datos analizados para cada recolección individual entre ${this.start} y ${this.end}.`;
    }
    return `Cette section du rapport contient les données analysées pour chaque collecte individuelle entre ${this.start} et ${this.end}.`;
  }

  loadImageAsBase64(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous'; // prevent CORS issues if needed
      img.src = url;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };

      img.onerror = reject;
    });
  }

  //Called after applying report filters
  async generatePdf() {
    //Int value to subtract to handle language changes in the PDF
    let headerModifier = 0;
    //Get Language
    const language = localStorage.getItem('language');
    //Set modifier value for headers
    switch (language) {
      case '"en"':
        headerModifier = 5;
        break;
      case '"es"':
        headerModifier = 0;
        break;
    }

    // Create a PDF using jsPDF
    const pdf = new jsPDF();
    //Set default page count
    let totalPages = 1;

    //page header constant to avoid code repetition
    const pageHeader = () => {
      // Add icon
      const icon = '../../assets/header-pdf.png'; // Update with your icon path
      const pageWidth = pdf.internal.pageSize.getWidth(); // Get page width
      const imgProps = pdf.getImageProperties(icon);
      const aspectRatio = imgProps.width / imgProps.height;
      const headerHeight = pageWidth / aspectRatio;
      pdf.addImage(icon, 'PNG', 0, 0, pageWidth, headerHeight); // Adjust position and size as needed
      const iconHeight = 10; // Adjust as needed
      const marginTop = 10; // Adjust as needed

      pdf.setFontSize(20);
      // Add text to the top right corner
      const text = this.collectionReport();
      const textWidth =
        (pdf.getStringUnitWidth(text) * 12) / pdf.internal.scaleFactor; // Calculate text width

      const marginRight = 10; // Adjust as needed
      const textX = pageWidth - textWidth - marginRight - 22;
      pdf.setTextColor(40, 142, 0); // Light green color (adjust as needed)
      pdf.text(text, textX, marginTop + iconHeight / 2 + -8);

      pdf.setTextColor(180); // reset text color

      //REMOVED
      // Draw a line below the text
      // const lineY = marginTop + iconHeight; // Adjust as needed
      // const lineWidth = pageWidth * 0.9; // 90% of page width
      // const lineX = (pageWidth - lineWidth) / 2; // Center the line
      // pdf.setDrawColor(180, 220, 160); // Set line color
      // pdf.setLineWidth(1); // Set line width
      // pdf.line(lineX, lineY, lineX + lineWidth, lineY);
    };

    // Define a function to increment the total number of pages
    const incrementTotalPages = () => {
      totalPages++;
    };

    // Set up a callback function to be executed before each page is added
    const beforePageAddCallback = () => {
      incrementTotalPages(); // Increment the total number of pages
    };

    // Repeat the header for the second page
    pageHeader();

    // Add header with icon
    const header = this.collectionStatisticsHeader();
    pdf.setFontSize(16);
    pdf.text(header, 10, 42);

    // Add informational body of text
    const infoText = this.infoTextStatistics();
    pdf.setFontSize(12);
    const textLines = pdf.splitTextToSize(infoText, 150); // Adjust width as needed
    pdf.text(textLines, 10, 48); // Adjust position as needed

    // Get the chart canvas as an image
    const collectionTotal = this.ensureCanvas(this.countCanvas, 'count').toDataURL('image/png');

    const collectedTonnage = this.ensureCanvas(this.tonnageCanvas, 'tonnage').toDataURL('image/png');
    const usageDistribution = this.ensureCanvas(this.usageCanvas, 'usage').toDataURL('image/png');
    const marketDistribution = this.ensureCanvas(this.marketCanvas, 'market').toDataURL('image/png');

    const usageTonnageDistribution = this.ensureCanvas(this.usageTonnageCanvas, 'usageTonnage').toDataURL('image/png');
    const marketTonnageDistribution = this.ensureCanvas(this.marketTonnageCanvas, 'marketTonnage').toDataURL('image/png');
    const subgroupTonnageDistribution = this.ensureCanvas(this.subgroupTonnageCanvas, 'subgroupTonnage').toDataURL('image/png');

    // Add header with icon
    const headerChart = this.totalCollectionsPerDevice();
    pdf.setFontSize(14);
    pdf.text(headerChart, 70 + headerModifier, 65);

    // Add the chart image to the PDF
    pdf.addImage(collectionTotal, 'PNG', 30, 70, 150, 75);

    const headerChart2 = this.totalTonnagePerDevice();
    pdf.setFontSize(14);
    pdf.text(headerChart2, 55, 185);
    // Add the chart image to the PDF
    pdf.addImage(collectedTonnage, 'PNG', 25, 190, 155, 75);

    beforePageAddCallback();
    pdf.addPage();

    // Repeat the header for the second page
    pageHeader();
    const headerChart3 = this.usageDistribution();
    pdf.setFontSize(14);
    pdf.text(headerChart3, 40 + headerModifier, 45);
    // Add the chart image to the PDF
    pdf.addImage(usageDistribution, 'PNG', 30, 50, 75, 75);

    const headerChart4 = this.marketDistribution();
    pdf.setFontSize(14);
    pdf.text(headerChart4, 125 + headerModifier, 45);
    // Add the chart image to the PDF
    pdf.addImage(marketDistribution, 'PNG', 110, 50, 75, 75);

    const headerChart6 = this.marketTonnageDistribution();
    pdf.setFontSize(14);
    pdf.text(headerChart6, 80 + headerModifier, 165);

    // Add the chart image to the PDF
    pdf.addImage(marketTonnageDistribution, 'PNG', 25, 170, 155, 75);

    beforePageAddCallback();
    pdf.addPage();

    // Repeat the header for the second page
    pageHeader();

    // Add header with icon
    const headerChart7 = this.usageTonnageDistribution();
    pdf.setFontSize(14);
    pdf.text(headerChart7, 80 + headerModifier, 45);
    // Add the chart image to the PDF
    pdf.addImage(usageTonnageDistribution, 'PNG', 30, 50, 150, 75);

    // Add header with icon
    const headerChart5 = this.subgroupTonnageDistribution();
    pdf.setFontSize(14);
    pdf.text(headerChart5, 80 + headerModifier, 165);
    // Add the chart image to the PDF
    pdf.addImage(subgroupTonnageDistribution, 'PNG', 25, 170, 155, 75);

    beforePageAddCallback();
    pdf.addPage();

    // Repeat the header for the second page
    pageHeader();
    // Add header with icon
    const header2 = this.collectionDataHeader();
    pdf.setFontSize(16);
    pdf.text(header2, 10, 42);

    // Add informational body of text
    const infoText2 = this.infoTextCollectionData();
    pdf.setFontSize(12);
    const textLines2 = pdf.splitTextToSize(infoText2, 150); // Adjust width as needed
    pdf.text(textLines2, 10, 48); // Adjust position as needed

    // Add data tables
    const data = this.collections;

    const tableStartY = 90; // Adjust position as needed
    //Calculate column width for data table
    const colWidth = pdf.internal.pageSize.getWidth() / data[0].length - 2;
    //Set default height values to be modified mathematically
    const rowHeight = 10;
    const lineHeight = 7;
    //Set start position for data table
    let currentY = tableStartY;

    //Column Headers constant for table data. To avoid code repetition
    const renderColumnHeaders = (currentY = 55) => {
      this.collections[0].forEach((header: string, cellIndex: number) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10); // Adjust font size as needed
        pdf.setTextColor(150); // Light grey color (adjust as needed)
        pdf.text(header, cellIndex * colWidth + 10, currentY + lineHeight + 10);

        // Add underline directly below the text
        const textWidth =
          (pdf.getStringUnitWidth(header) * 12) / pdf.internal.scaleFactor;
        const startX = cellIndex * colWidth + 10;
        const endX = startX + textWidth;
        const lineY = currentY + lineHeight + 12; // Adjust the Y position to drop the line below the text

        pdf.setLineWidth(0.1);
        pdf.setDrawColor(150); // Set line color
        pdf.line(startX, lineY, endX, lineY);
      });
    };

    // Render Column headers
    let columnHeaders: string[] = [];
    let headersRendered = false;
    renderColumnHeaders();
    let first = true;
    this.collections.forEach((row: any, rowIndex: any) => {
      // Check if there's enough space for the row, otherwise start a new page
      if (currentY + rowHeight > pdf.internal.pageSize.getHeight() - 25) {
        first = false;
        beforePageAddCallback();
        pdf.addPage(); // Start a new page
        // Add icon
        // Repeat the header for the second page
        pageHeader();
        currentY = 25; // Reset Y position for the table

        // Render column headers at the beginning of each new page
        renderColumnHeaders(currentY);
        currentY += rowHeight; // Move Y position down after rendering headers
      }

      // Render row cells
      Object.keys(row).forEach((key: string, cellIndex: number) => {
        const cell = row[key];

        // Set font style for the first row to extract column headers
        if (rowIndex === 0 && !headersRendered) {
          columnHeaders.push(cell);
        }

        let modifier = 0;
        if (first) {
          modifier = 25;
        }

        // Render row data
        if (rowIndex !== 0) {
          if (cellIndex === 0) {
            currentY += rowHeight;
          }
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(0); // Black color
          pdf.setFontSize(8); // Adjust font size as needed
          pdf.text(
            cell,
            cellIndex * colWidth + 10,
            currentY + lineHeight - modifier
          );
        }
      });
    });

    // Calculate line positions
    const lineStartX = pdf.internal.pageSize.getWidth() * 0.05; // Start 5% from the left edge
    const lineEndX = pdf.internal.pageSize.getWidth() * 0.95; // End 5% from the right edge
    const lineY = pdf.internal.pageSize.getHeight() - 10; // Position the line above the footer

    // Set draw color for the line
    pdf.setDrawColor(180, 220, 160);

    // Draw the line
    pdf.line(lineStartX, lineY, lineEndX, lineY);

    //Translate pagination
    let page = 'Page';
    let adverb = 'sur';

    switch (language) {
      case '"en"':
        adverb = 'of';
        break;
      case '"es"':
        page = 'Página';
        adverb = 'de';
        break;
    }

    // After the PDF is generated, add page numbers to each page
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i); // Go to specified page
      pdf.setDrawColor(180, 220, 160);
      // Draw the line
      pdf.line(lineStartX, lineY, lineEndX, lineY);
      // Get the current local date
      const currentDate = new Date().toLocaleDateString();

      pdf.setTextColor(150);
      // Add current local date on the right side of the footer
      pdf.text(
        currentDate,
        pdf.internal.pageSize.getWidth() - 195,
        pdf.internal.pageSize.getHeight() - 5
      );

      // Add page number on the right
      pdf.text(
        `${page} ${i} ${adverb} ${totalPages}`,
        pdf.internal.pageSize.getWidth() - 30,
        pdf.internal.pageSize.getHeight() - 5
      );
    }

    // Save or display the PDF
    pdf.save(
      `${this.fileNameCollectionReport()}-${this.start}-${this.end}.pdf`
    );
    this.generatingPDF = false;
    this.clearCanvas();
  }

  async generateInstallationInvoicePdf(data: {
    clientName: string;
    clientId: string;
    clientAddress?: string;
    devices: { thingName: string; address: string; usage: string }[];
    deviceFee: number;
    additionalFee: number;
  }): Promise<void> {
    // Build the installation invoice PDF with per-device fees.
    if (this.generatingPDF) return;
    this.generatingPDF = true;

    const pdf = new jsPDF(); // New PDF instance per invoice.
    const headerHeight = this.addStandardHeader(
      pdf,
      this.translate.instant('invoicePdf.installationTitle')
    );

    const pageWidth = pdf.internal.pageSize.getWidth(); // Page width for layout calculations.
    let cursorY = headerHeight + 20; // Cursor for vertical layout flow.

    const issuedDate = new Date(); // Invoice generation timestamp.
    const invoiceDate = issuedDate.toISOString().slice(0, 10); // YYYY-MM-DD.
    const dueDate = new Date(issuedDate.getTime() + 90 * 24 * 60 * 60 * 1000) // +90 days.
      .toISOString()
      .slice(0, 10);
    const invoiceId = `INV-${Date.now()}`; // Simple unique invoice number.

    const clientLabel = this.translate.instant('invoicePdf.client');
    const invoiceNumberLabel = this.translate.instant('invoicePdf.invoiceNumber');
    const dateLabel = this.translate.instant('invoicePdf.date');
    const dueLabel = this.translate.instant('invoicePdf.due');
    const renderInvoiceMeta = (y: number): number => {
      // Render header metadata block on each page.
      pdf.setFontSize(11);
      pdf.text(`${clientLabel}: ${data.clientName}`, 14, y);
      pdf.text(`${invoiceNumberLabel}: ${invoiceId}`, 14, y + 6);
      pdf.text(`${dateLabel}: ${invoiceDate}`, pageWidth - 70, y);
      pdf.text(`${dueLabel}: ${dueDate}`, pageWidth - 70, y + 6);
      return y + 14;
    };

    cursorY = renderInvoiceMeta(cursorY); // First-page metadata.

    const colX = [14, 55, 115, 155]; // Table column starts.
    pdf.setFontSize(10);
    pdf.setTextColor(120);
    // Table header row.
    pdf.text(this.translate.instant('invoicePdf.device'), colX[0], cursorY);
    pdf.text(this.translate.instant('invoicePdf.address'), colX[1], cursorY);
    pdf.text(this.translate.instant('invoicePdf.usage'), colX[2], cursorY);
    const amountColumnX = colX[2] + 45 + pageWidth * 0.05; // Right-aligned amount column.
    pdf.text(this.translate.instant('invoicePdf.amount'), amountColumnX, cursorY, {
      align: 'right',
    });
    pdf.setTextColor(0);

    cursorY += 6;
    pdf.setLineWidth(0.1);
    pdf.setDrawColor(160);
    pdf.line(14, cursorY, pageWidth - 14, cursorY);
    cursorY += 6;

    const deviceFee = Number.isFinite(data.deviceFee) ? data.deviceFee : 0;
    const additionalFee = Number.isFinite(data.additionalFee) ? data.additionalFee : 0;
    let subtotal = 0;

    pdf.setFontSize(9);
    const rowHeight = 7;
    const rowFill = [230, 245, 230];
    for (const [index, device] of data.devices.entries()) {
      const lineTotal = deviceFee;
      subtotal += lineTotal;
      const address = device.address || '-';
      const usage = device.usage || '-';

      const addressLines = pdf.splitTextToSize(address, 55); // Wrap long addresses.
      const usageLines = pdf.splitTextToSize(usage, 40); // Wrap usage text.
      const maxLines = Math.max(addressLines.length, usageLines.length, 1);
      const blockHeight = rowHeight * maxLines;

      if (index % 2 === 1) {
        pdf.setFillColor(rowFill[0], rowFill[1], rowFill[2]);
        pdf.rect(14, cursorY - 4, pageWidth - 28, blockHeight + 2, 'F');
      }

      pdf.text(device.thingName, colX[0], cursorY);
      pdf.text(addressLines, colX[1], cursorY);
      pdf.text(usageLines, colX[2], cursorY);
      pdf.text(`$${lineTotal.toFixed(2)}`, amountColumnX, cursorY, {
        align: 'right',
      });

      cursorY += blockHeight + 2;

      if (cursorY > pdf.internal.pageSize.getHeight() - 30) {
        pdf.addPage();
        this.addStandardHeader(pdf, 'Invoice');
        cursorY = headerHeight + 20;
        cursorY = renderInvoiceMeta(cursorY); // Repeat metadata on new pages.
      }
    }

    const baseTotal = subtotal + additionalFee; // Pre-tax subtotal.
    const taxRate = this.resolveTaxRate(data.clientAddress || ''); // Province/federal rates.
    const federalTaxAmount = taxRate ? (baseTotal * taxRate.federal) / 100 : 0;
    const provincialTaxAmount = taxRate ? (baseTotal * taxRate.provincial) / 100 : 0;
    const total = baseTotal + federalTaxAmount + provincialTaxAmount;
    cursorY += 6;
    pdf.setDrawColor(160);
    pdf.line(14, cursorY, pageWidth - 14, cursorY);
    cursorY += 6;

    const totalLabelX = colX[2] + 2; // Align totals labels with usage column.
    const totalValueX = amountColumnX + 2; // Align totals values with amount column.
    const totalRowHeight = 6;

    const federalLabel =
      taxRate?.federalLabel || this.translate.instant('invoicePdf.federalTaxSubtotal');
    const provincialLabel =
      taxRate?.provincialLabel || this.translate.instant('invoicePdf.provincialTaxSubtotal');

    const totalRows: Array<{ label: string; value: number; bold?: boolean }> = [
      // Totals block rendered inside table flow.
      { label: this.translate.instant('invoicePdf.subtotal'), value: subtotal },
      { label: this.translate.instant('invoicePdf.additional'), value: additionalFee },
      {
        label: `${federalLabel}${taxRate ? ` (${taxRate.federal.toFixed(3)}%)` : ''}`,
        value: federalTaxAmount,
      },
      {
        label: `${provincialLabel}${taxRate ? ` (${taxRate.provincial.toFixed(3)}%)` : ''}`,
        value: provincialTaxAmount,
      },
      { label: this.translate.instant('invoicePdf.total'), value: total, bold: true },
    ];

    const requiredHeight = totalRows.length * totalRowHeight + 6; // Keep totals together.
    const pageBottom = pdf.internal.pageSize.getHeight() - 14; // Footer safe area.
    if (cursorY + requiredHeight > pageBottom) {
      pdf.addPage();
      this.addStandardHeader(pdf, 'Invoice');
      cursorY = headerHeight + 20;
      cursorY = renderInvoiceMeta(cursorY);
      pdf.setDrawColor(160);
      pdf.line(14, cursorY - 6, pageWidth - 14, cursorY - 6);
    }

    const writeTotalRow = (label: string, value: number, isBold = false) => {
      // Render totals row with dotted leader line.
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      pdf.text(label, totalLabelX, cursorY);
      pdf.text(`$${value.toFixed(2)}`, totalValueX, cursorY, { align: 'right' });

      const labelWidth = pdf.getTextWidth(label); // Used to start dotted leaders.
      const lineStart = totalLabelX + labelWidth + 3;
      const lineEnd = totalValueX - 14;
      if (lineEnd > lineStart) {
        pdf.setDrawColor(170);
        const dash = (pdf as any).setLineDash;
        if (typeof dash === 'function') {
          dash.call(pdf, [1, 1], 0);
        }
        pdf.line(lineStart, cursorY, lineEnd, cursorY);
        if (typeof dash === 'function') {
          dash.call(pdf, [], 0);
        }
      }

      cursorY += totalRowHeight;
    };

    pdf.setFontSize(9);
    for (const row of totalRows) {
      writeTotalRow(row.label, row.value, Boolean(row.bold));
    }
    pdf.setFont('helvetica', 'normal');

    this.addInvoiceFooter(pdf); // Page numbers + copyright.
    pdf.save(`installation-invoice-${data.clientId}-${Date.now()}.pdf`);
    this.generatingPDF = false;
  }

  async generateSubscriptionInvoicePdf(data: {
    clientName: string;
    clientId: string;
    clientAddress?: string;
    devices: { thingName: string; address: string; usage: string }[];
    subscriptionFee: number;
    serviceFee: number;
    periodStart?: string;
    periodEnd?: string;
  }): Promise<void> {
    // Build the subscription invoice PDF with per-device and service fees.
    if (this.generatingPDF) return;
    this.generatingPDF = true;

    const pdf = new jsPDF(); // New PDF instance per invoice.
    const headerHeight = this.addStandardHeader(
      pdf,
      this.translate.instant('invoicePdf.subscriptionTitle')
    );

    const pageWidth = pdf.internal.pageSize.getWidth();
    let cursorY = headerHeight + 20; // Cursor for vertical layout flow.

    const issuedDate = new Date();
    const invoiceDate = issuedDate.toISOString().slice(0, 10);
    const dueDate = new Date(issuedDate.getTime() + 90 * 24 * 60 * 60 * 1000)
      .toISOString()
      .slice(0, 10);
    const invoiceId = `INV-${Date.now()}`;

    const clientLabel = this.translate.instant('invoicePdf.client');
    const invoiceNumberLabel = this.translate.instant('invoicePdf.invoiceNumber');
    const dateLabel = this.translate.instant('invoicePdf.date');
    const dueLabel = this.translate.instant('invoicePdf.due');
    const renderInvoiceMeta = (y: number): number => {
      // Render header metadata block on each page.
      pdf.setFontSize(11);
      pdf.text(`${clientLabel}: ${data.clientName}`, 14, y);
      pdf.text(`${invoiceNumberLabel}: ${invoiceId}`, 14, y + 6);
      pdf.text(`${dateLabel}: ${invoiceDate}`, pageWidth - 70, y);
      pdf.text(`${dueLabel}: ${dueDate}`, pageWidth - 70, y + 6);
      if (data.periodStart && data.periodEnd) {
        const periodLabel = this.translate.instant('invoicePdf.period');
        pdf.text(
          `${periodLabel}: ${data.periodStart} - ${data.periodEnd}`,
          14,
          y + 12
        );
        return y + 20;
      }
      return y + 14;
    };

    cursorY = renderInvoiceMeta(cursorY);

    const colX = [14, 55, 115, 155];
    pdf.setFontSize(10);
    pdf.setTextColor(120);
    pdf.text(this.translate.instant('invoicePdf.device'), colX[0], cursorY);
    pdf.text(this.translate.instant('invoicePdf.address'), colX[1], cursorY);
    pdf.text(this.translate.instant('invoicePdf.usage'), colX[2], cursorY);
    const amountColumnX = colX[2] + 45 + pageWidth * 0.05;
    pdf.text(this.translate.instant('invoicePdf.amount'), amountColumnX, cursorY, {
      align: 'right',
    });
    pdf.setTextColor(0);

    cursorY += 6;
    pdf.setLineWidth(0.1);
    pdf.setDrawColor(160);
    pdf.line(14, cursorY, pageWidth - 14, cursorY);
    cursorY += 6;

    const subscriptionFee = Number.isFinite(data.subscriptionFee)
      ? data.subscriptionFee
      : 0;
    const serviceFee = Number.isFinite(data.serviceFee) ? data.serviceFee : 0;
    let subtotal = 0;

    pdf.setFontSize(9);
    const rowHeight = 7;
    const rowFill = [230, 245, 230];
    for (const [index, device] of data.devices.entries()) {
      const lineTotal = subscriptionFee;
      subtotal += lineTotal;
      const address = device.address || '-';
      const usage = device.usage || '-';

      const addressLines = pdf.splitTextToSize(address, 55);
      const usageLines = pdf.splitTextToSize(usage, 40);
      const maxLines = Math.max(addressLines.length, usageLines.length, 1);
      const blockHeight = rowHeight * maxLines;

      if (index % 2 === 1) {
        pdf.setFillColor(rowFill[0], rowFill[1], rowFill[2]);
        pdf.rect(14, cursorY - 4, pageWidth - 28, blockHeight + 2, 'F');
      }

      pdf.text(device.thingName, colX[0], cursorY);
      pdf.text(addressLines, colX[1], cursorY);
      pdf.text(usageLines, colX[2], cursorY);
      pdf.text(`$${lineTotal.toFixed(2)}`, amountColumnX, cursorY, {
        align: 'right',
      });

      cursorY += blockHeight + 2;

      if (cursorY > pdf.internal.pageSize.getHeight() - 30) {
        pdf.addPage();
        this.addStandardHeader(
          pdf,
          this.translate.instant('invoicePdf.subscriptionTitle')
        );
        cursorY = headerHeight + 20;
        cursorY = renderInvoiceMeta(cursorY);
      }
    }

    const baseTotal = subtotal + serviceFee;
    const taxRate = this.resolveTaxRate(data.clientAddress || '');
    const federalTaxAmount = taxRate ? (baseTotal * taxRate.federal) / 100 : 0;
    const provincialTaxAmount = taxRate ? (baseTotal * taxRate.provincial) / 100 : 0;
    const total = baseTotal + federalTaxAmount + provincialTaxAmount;

    cursorY += 6;
    pdf.setDrawColor(160);
    pdf.line(14, cursorY, pageWidth - 14, cursorY);
    cursorY += 6;

    const totalLabelX = colX[2] + 2;
    const totalValueX = amountColumnX + 2;
    const totalRowHeight = 6;

    const federalLabel =
      taxRate?.federalLabel || this.translate.instant('invoicePdf.federalTaxSubtotal');
    const provincialLabel =
      taxRate?.provincialLabel || this.translate.instant('invoicePdf.provincialTaxSubtotal');

    const totalRows: Array<{ label: string; value: number; bold?: boolean }> = [
      // Totals block rendered inside table flow.
      { label: this.translate.instant('invoicePdf.subtotal'), value: subtotal },
      { label: this.translate.instant('invoicePdf.serviceFee'), value: serviceFee },
      {
        label: `${federalLabel}${taxRate ? ` (${taxRate.federal.toFixed(3)}%)` : ''}`,
        value: federalTaxAmount,
      },
      {
        label: `${provincialLabel}${taxRate ? ` (${taxRate.provincial.toFixed(3)}%)` : ''}`,
        value: provincialTaxAmount,
      },
      { label: this.translate.instant('invoicePdf.total'), value: total, bold: true },
    ];

    const requiredHeight = totalRows.length * totalRowHeight + 6;
    const pageBottom = pdf.internal.pageSize.getHeight() - 14;
    if (cursorY + requiredHeight > pageBottom) {
      pdf.addPage();
      this.addStandardHeader(
        pdf,
        this.translate.instant('invoicePdf.subscriptionTitle')
      );
      cursorY = headerHeight + 20;
      cursorY = renderInvoiceMeta(cursorY);
      pdf.setDrawColor(160);
      pdf.line(14, cursorY - 6, pageWidth - 14, cursorY - 6);
    }

    const writeTotalRow = (label: string, value: number, isBold = false) => {
      // Render totals row with dotted leader line.
      pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
      pdf.text(label, totalLabelX, cursorY);
      pdf.text(`$${value.toFixed(2)}`, totalValueX, cursorY, { align: 'right' });

      const labelWidth = pdf.getTextWidth(label);
      const lineStart = totalLabelX + labelWidth + 3;
      const lineEnd = totalValueX - 14;
      if (lineEnd > lineStart) {
        pdf.setDrawColor(170);
        const dash = (pdf as any).setLineDash;
        if (typeof dash === 'function') {
          dash.call(pdf, [1, 1], 0);
        }
        pdf.line(lineStart, cursorY, lineEnd, cursorY);
        if (typeof dash === 'function') {
          dash.call(pdf, [], 0);
        }
      }

      cursorY += totalRowHeight;
    };

    pdf.setFontSize(9);
    for (const row of totalRows) {
      writeTotalRow(row.label, row.value, Boolean(row.bold));
    }
    pdf.setFont('helvetica', 'normal');

    this.addInvoiceFooter(pdf); // Page numbers + copyright.
    pdf.save(`subscription-invoice-${data.clientId}-${Date.now()}.pdf`);
    this.generatingPDF = false;
  }

  private addInvoiceFooter(pdf: jsPDF): void {
    // Footer runs after content generation to stamp each page.
    const totalPages = pdf.getNumberOfPages();
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const footerY = pageHeight - 6;
    const year = new Date().getFullYear();
    const pageLabel = this.translate.instant('invoicePdf.page');
    const pageOfLabel = this.translate.instant('invoicePdf.pageOf');
    const copyrightLabel = this.translate.instant('invoicePdf.copyright', {
      year,
    });

    pdf.setFontSize(8);
    pdf.setTextColor(150);

    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.text(copyrightLabel, 14, footerY);
      pdf.text(`${pageLabel} ${i} ${pageOfLabel} ${totalPages}`, pageWidth - 14, footerY, {
        align: 'right',
      });
    }

    pdf.setTextColor(0);
  }

  private resolveTaxRate(address: string): CanadaTaxRate | null {
    // Determine province tax by matching code or province name in the address.
    if (!address) return null;
    const normalized = address.toUpperCase();

    const byCode = CANADA_TAX_RATES.find((rate) =>
      new RegExp(`\\b${rate.code}\\b`).test(normalized)
    );
    if (byCode) return byCode;

    const byName = CANADA_TAX_RATES.find((rate) =>
      normalized.includes(rate.province.toUpperCase())
    );
    return byName || null;
  }

  // Function that take a date in string to convert it into gibInt timestamp
  dateToUnixTimestamp(dateString: string): number {
    // Parse the date string into a Date object
    const date = new Date(dateString);

    // Get the UTC timestamp in milliseconds
    const timestampInMilliseconds = date.getTime();

    // Convert milliseconds to seconds and return
    return Math.floor(timestampInMilliseconds / 1000);
  }

  // Fucntion that return the date in correct universal time UTC
  formatDate(date: string): string {
    // Convert input date string to a date object
    const dateObject = new Date(date);

    // Adjust the date to represent the correct local date
    dateObject.setUTCFullYear(dateObject.getUTCFullYear());
    dateObject.setUTCMonth(dateObject.getUTCMonth());
    dateObject.setUTCDate(dateObject.getUTCDate() + 1); // Increment by one day

    // Format the date as a string
    const dateString = dateObject.toLocaleDateString(
      this.charts.getDateLanguage(),
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    );

    return dateString;
  }

  //Apply filters to generated report
  async applyReportFilters() {
    this.history = [];
    this.counts = [];
    this.collections = [];
    this.usageData = [];
    this.usageLabels = [];
    this.marketLabels = [];
    this.marketData = [];
    this.collectionsData = [];
    this.collectionTonnage = [];
    this.thingCollectionLabels = [];
    this.countsLabels = [];
    this.countsData = [];

    const start = this.dateToUnixTimestamp(this.start + 'T00:00:00');

    const end = this.dateToUnixTimestamp(this.end + 'T23:59:59');

    this.start = this.formatDate(this.start);

    this.end = this.formatDate(this.end);

    let things = this.things;

    try {
      // Get repports from DB
      const res = await this.reports.getReportData(things, start, end);

      let label = '';
      let index = 0;
      let first = true;

      const subgroupMap: Record<string, SubGroup> = {};

      for (const item of JSON.parse(res.subgroups)) {
        const { device_sub_group, sub_group_name, thing_name } = item;

        if (!subgroupMap[device_sub_group]) {
          subgroupMap[device_sub_group] = {
            device_sub_group,
            sub_group_name,
            things: [],
          };
        }

        subgroupMap[device_sub_group].things.push({
          thing_name,
          tonnage: 0, // Replace this with actual tonnage data if available
        });
      }

      const subgroups: SubGroup[] = Object.values(subgroupMap);

      const binData: BinData[] = JSON.parse(res.bins);

      for (const usage of JSON.parse(res.usage)) {
        this.usageData.push(usage.bin_usage_count);
        this.usageLabels.push(this.binUsage(usage.bin_usage));
      }

      for (const market of JSON.parse(res.markets)) {
        this.marketData.push(market.market_segment_count);
        this.marketLabels.push(this.marketSegment(market.market_segment));
      }

      //Set translated table headers
      this.setCollectionTableHeaders();

      for (const collection of JSON.parse(res.collections)) {
        const bin = this.filterBinData(binData, collection.thing_name)[0];
        if (bin.bin_height != null && !isNaN(Number(bin.bin_height))) {
          const cubicMetersOfWaste = this.calculateCubicMeters(
            bin.total_volume,
            collection.preceding_dst,
            bin.bin_height
          );
          const tonnage =
            this.calculateWasteCollection(bin.bin_usage, cubicMetersOfWaste) *
            0.001;

          // Add tonnage to corresponding thing_name in subgroups array
          for (const group of subgroups) {
            const thing = group.things.find(
              (t) => t.thing_name === collection.thing_name
            );
            if (thing) {
              const roundedTonnage = Number(tonnage.toFixed(2));
              thing.tonnage += roundedTonnage;
              thing.tonnage = Number(thing.tonnage.toFixed(2));
              break; // Exit early once the correct thing is found
            }
          }

          this.collections.push([
            collection.thing_name,
            `${(
              ((bin.bin_height - collection.preceding_dst) / bin.bin_height) *
              100
            ).toFixed(2)}%`,
            new Date(collection.preceding_timestamp * 1000).toLocaleDateString(
              this.charts.getDateLanguage(),
              {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              }
            ),
            `${bin.threshold}%`,
            `${bin.total_volume}L`,
            `${this.binUsage(bin.bin_usage)}`,
            `${this.marketSegment(bin.market_segment)}`,
            tonnage.toFixed(2),
          ]);

          this.masterArray.push({
            thing_name: collection.thing_name,
            timestamp: collection.dst_timestamp,
            usage: this.binUsage(bin.bin_usage),
            market: this.marketSegment(bin.market_segment),
            tonnage: Number(tonnage.toFixed(2)),
          });
        }
      }

      this.subgroups = subgroups;

      // Reset arrays before populating
      this.subgroupTonnageLabels = [];
      this.subgroupTonnageData = [];

      for (const subgroup of this.subgroups) {
        const totalTonnage = subgroup.things.reduce(
          (sum, thing) => sum + thing.tonnage,
          0
        );
        this.subgroupTonnageLabels.push(subgroup.sub_group_name);
        this.subgroupTonnageData.push(Number(totalTonnage.toFixed(2))); // Round to 2 decimals
      }

      // Reset arrays before populating
      this.usageTonnageLabels = [];
      this.usageTonnageData = [];
      this.marketTonnageLabels = [];
      this.marketTonnageData = [];

      // Temporary maps to sum tonnage
      const usageMap: Record<string, number> = {};
      const marketMap: Record<string, number> = {};

      for (const item of this.masterArray) {
        // Sum by usage
        if (!usageMap[item.usage]) {
          usageMap[item.usage] = 0;
        }
        usageMap[item.usage] += item.tonnage;

        // Sum by market
        if (!marketMap[item.market]) {
          marketMap[item.market] = 0;
        }
        marketMap[item.market] += item.tonnage;
      }

      // Populate chart arrays with 2-decimal precision
      for (const usage in usageMap) {
        this.usageTonnageLabels.push(usage);
        this.usageTonnageData.push(Number(usageMap[usage].toFixed(2)));
      }

      for (const market in marketMap) {
        this.marketTonnageLabels.push(market);
        this.marketTonnageData.push(Number(marketMap[market].toFixed(2)));
      }

      for (const counts of JSON.parse(res.counts)) {
        const bin = this.filterBinData(binData, counts.thing_name)[0];

        if (bin.bin_height != null && !isNaN(Number(bin.bin_height))) {
          this.counts.push(counts);
          this.countsLabels.push(counts.thing_name);
          this.countsData.push(counts.collection_count);
          this.thingCollectionLabels.push(counts.thing_name);
          const cubicMetersOfWaste =
            this.calculateCubicMeters(
              bin.total_volume,
              counts.avg_preceding_dst,
              bin.bin_height
            ) * counts.collection_count;
          const tonnage =
            this.calculateWasteCollection(bin.bin_usage, cubicMetersOfWaste) *
            0.001;
          this.collectionTonnage.push(tonnage);
        }
      }

      await this.initCollectionCountsCanvas();
      await this.initTonnageCanvas();
      await this.initUsageCanvas();
      await this.initMarketCanvas();
      await this.initSubgroupTonnageCanvas();
      await this.initMarketTonnageCanvas();
      await this.initUsageTonnageCanvas();

      // console.log(this.collections);
      // console.log(this.usageTonnageLabels, this.usageTonnageData);
      // console.log(this.marketTonnageLabels, this.marketTonnageData);
      // console.log(this.subgroupTonnageLabels, this.subgroupTonnageData);
      setTimeout(() => {
        this.generatePdf();
      }, 2000);
      this.generatingPDF = false;
    } catch (error) {
      console.error('API Error: ', error);
      this.generatingPDF = false;
    }
  }

  filterBinData(array: BinData[], thingName: string): any {
    return array
      .filter((item) => item.thing_name === thingName)
      .map(
        ({
          bin_id,
          bin_gps,
          bin_location,
          bin_usage,
          bin_address,
          legal_name,
          market_segment,
          threshold,
          bin_height,
          total_volume,
        }) => ({
          bin_id,
          bin_gps,
          bin_location,
          bin_usage,
          bin_address,
          legal_name,
          market_segment,
          threshold,
          bin_height,
          total_volume,
        })
      );
  }

  // Function that will calculate the volume if all field are well filled
  calculateCubicMeters(binVolume: number, dst: number, binHeight: number) {
    const multiplier = 1 - dst / binHeight;
    return (binVolume / 1000) * multiplier;
  }

  //Translate Table Headers for PDF
  setCollectionTableHeaders() {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        this.collections.push([
          'Device',
          'Capacity',
          'Date',
          'Threshold',
          'Volume',
          'Usage',
          'Market',
          'Tonnage',
        ]);
        break;
      case '"es"':
        this.collections.push([
          'Dispositivo',
          'Capacidad',
          'Fecha',
          'Umbral',
          'Volumen',
          'Uso',
          'Mercado',
          'Tonelaje',
        ]);
        break;
      default:
        this.collections.push([
          'Appareil',
          'Capacité',
          'Date',
          'Seuil',
          'Volume',
          'Utilisation',
          'Marché',
          'Tonnage',
        ]);
        break;
    }
  }

  //Market segment labels/language
  marketSegment(id: string): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        switch (id) {
          case 'TE':
            return 'Technology';
          case 'FO':
            return 'Restaurant';
          case 'ST':
            return 'Commerce';
          case 'CO':
            return 'Construction';
          case 'MA':
            return 'Manufacturing';
          case 'RE':
            return 'Residential';
          case 'MU':
            return 'Municipal';
          case 'SC':
            return 'Education';
          default:
            return 'N/A';
        }
      case '"es"':
        switch (id) {
          case 'TE':
            return 'Tecnología';
          case 'FO':
            return 'Restaurante';
          case 'ST':
            return 'Comercio';
          case 'CO':
            return 'Construcción';
          case 'MA':
            return 'Fabricación';
          case 'RE':
            return 'Residencial';
          case 'MU':
            return 'Municipal';
          case 'SC':
            return 'Educación';
          default:
            return 'N/A';
        }
      default:
        switch (id) {
          case 'TE':
            return 'Technologie';
          case 'FO':
            return 'Restaurant';
          case 'ST':
            return 'Commerce';
          case 'CO':
            return 'Construction';
          case 'MA':
            return 'Fabrication';
          case 'RE':
            return 'Résidentiel';
          case 'MU':
            return 'Municipal';
          case 'SC':
            return 'Éducation';
          default:
            return 'N/A';
        }
    }
  }

  //Usage Labels/language
  binUsage(id: string): string {
    const language = localStorage.getItem('language');
    switch (language) {
      case '"en"':
        switch (id) {
          case 'MT':
            return 'Metal';
          case 'PC':
            return 'Paper';
          case 'PL':
            return 'Plastic';
          case 'CN':
            return 'Cans';
          case 'GL':
            return 'Glass';
          case 'OR':
            return 'Organic';
          case 'GW':
            return 'Waste';
          case 'GR':
            return 'Recycling';
          case 'BA':
            return 'Batteries';
          case 'MW':
            return 'Medical Waste';
          case 'RB':
            return 'Rubber';
          case 'TW':
            return 'Electronics';
          case 'CM':
            return 'Construction';
          case 'CL':
            return 'Clothing';
          case 'CB':
            return 'Cardboard';
          default:
            return 'N/A';
        }
      case '"es"':
        switch (id) {
          case 'MT':
            return 'Metal';
          case 'PC':
            return 'Papel';
          case 'PL':
            return 'Plástico';
          case 'CN':
            return 'Latas';
          case 'GL':
            return 'Vidrio';
          case 'OR':
            return 'Orgánicos';
          case 'GW':
            return 'Generales';
          case 'GR':
            return 'Reciclaje';
          case 'BA':
            return 'Baterías';
          case 'MW':
            return 'Médicos';
          case 'RB':
            return 'Goma';
          case 'TW':
            return 'Electrónicos';
          case 'CM':
            return 'Construcción';
          case 'CL':
            return 'Ropa';
          case 'CB':
            return 'Cartón';
          default:
            return 'N/A';
        }
      default:
        switch (id) {
          case 'MT':
            return 'Métal';
          case 'PC':
            return 'Papier';
          case 'PL':
            return 'Plastique';
          case 'CN':
            return 'Boîtes';
          case 'GL':
            return 'Verre';
          case 'OR':
            return 'Organiques';
          case 'GW':
            return 'Déchets';
          case 'GR':
            return 'Recyclage';
          case 'BA':
            return 'Batteries';
          case 'MW':
            return 'Médicaux';
          case 'RB':
            return 'Caoutchouc';
          case 'TW':
            return 'Électroniques';
          case 'CM':
            return 'Construction';
          case 'CL':
            return 'Vêtements';
          case 'CB':
            return 'Carton';
          default:
            return 'N/A';
        }
    }
  }

  //Calculate estimate tonnage of collected waste
  calculateWasteCollection(
    usage: string,
    totalCubicMetersOfWaste: number
  ): number {
    const weightPerCubicMeter = this.getWeightByValue(usage);
    if (!isNaN(totalCubicMetersOfWaste) && weightPerCubicMeter !== undefined) {
      return totalCubicMetersOfWaste * weightPerCubicMeter;
    } else {
      return 0;
    }
  }

  //Get the average weight for the bin usage type
  getWeightByValue(value: string): number {
    const bin = BinUsage.find((bin) => bin.value === value);
    return bin?.weight || 0;
  }

  //Reset all graph canvas'
  clearCanvas(nullify = true) {
    this.tonnageChartInstance.clear();
    this.countChartInstance.clear();
    this.marketChartInstance.clear();
    this.usageChartInstance.clear();

    this.tonnageChartInstance.config.data = this.cleanData;

    this.tonnageChartInstance.update();
    this.countChartInstance.update();
    this.marketChartInstance.update();
    this.usageChartInstance.update();

    this.tonnageChartInstance.destroy();
    this.countChartInstance.destroy();
    this.marketChartInstance.destroy();
    this.usageChartInstance.destroy();

    if (nullify) {
      this.tonnageChartInstance = null; // Clear the reference
      this.countChartInstance = null;
      this.marketChartInstance = null;
      this.usageChartInstance = null;
    }
  }
}
